import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";


//https://giggster.com/guide/complementary-colors/
//http://mcg.mbitson.com/#!?mcgpalette0=%2314842f

// color design tokens export
export const tokensDark = {
  grey: {
    0: "#ffffff", // manually adjusted
    10: "#f6f6f6", // manually adjusted
    50: "#f0f0f0", // manually adjusted
    100: "#e0e0e0",
    200: "#c2c2c2",
    300: "#a3a3a3",
    400: "#858585",
    500: "#666666",
    600: "#525252",
    700: "#3d3d3d",
    800: "#292929",
    900: "#141414",
    1000: "#000000", // manually adjusted
  },
  secondary: {
    50: '#e3f0e6',
    100: '#b9dac1',
    200: '#8ac297',
    300: '#5ba96d',
    400: '#37964e',
    500: '#14842f', //main secondary colour 
    600: '#127c2a',
    700: '#0e7123',
    800: '#0b671d',
    900: '#065412',
  },


  primary1:{
    50: '#f5e5e3',
  100: '#e7beb8',
  200: '#d79389',
  300: '#c7685a',
  400: '#bb4736',
  500: '#af2713', //OpsBlox red
  600: '#a82311',
  700: '#9f1d0e',
  800: '#96170b',
  900: '#860e06',

  },

  primary:{

  50: '#e0f6fd',
  100: '#b3e7fb',
  200: '#80d8f8',
  300: '#4dc8f5',
  400: '#26bcf2',
  500: '#00b0f0',
  600: '#00a9ee',
  700: '#00a0ec',
  800: '#0097e9',
  900: '#0087e5',
},

  secondary3: {
    // yellow
    50: "#f0f0f0", // manually adjusted
    100: "#fff6e0",
    200: "#ffedc2",
    300: "#ffe3a3",
    400: "#ffda85",
    500: "#ffd166",
    600: "#cca752",
    700: "#997d3d",
    800: "#665429",
    900: "#332a14",
  },

};

// function that reverses the color palette
function reverseTokens(tokensDark) {
  const reversedTokens = {};
  Object.entries(tokensDark).forEach(([key, val]) => {
    const keys = Object.keys(val);
    const values = Object.values(val);
    const length = keys.length;
    const reversedObj = {};
    for (let i = 0; i < length; i++) {
      reversedObj[keys[i]] = values[length - i - 1];
    }
    reversedTokens[key] = reversedObj;
  });
  return reversedTokens;
}
export const tokensLight = reverseTokens(tokensDark);


export const tokens = (mode) =>{

  return (mode === "dark" 
  ?{
    ...tokensDark
  }
  :
  {
    ...tokensLight
  })
} 

// ---------------------------------------------------------------------------
// Brand tint. #00b0f0 is retained as the OpsBlox brand cyan for light fills
// and illustrative accents only - never for text or filled controls, where it
// measures 2.48:1 on white and fails WCAG AA (and AA-large).
// ---------------------------------------------------------------------------
export const brandTint = tokensDark.primary[500]; // #00b0f0

// ---------------------------------------------------------------------------
// Semantic roles for LIGHT MODE ONLY.
//
// Dark mode deliberately leaves error/warning/info/success undefined so they
// keep falling through to MUI's dark defaults, which render correctly. Spec
// section 6 forbids re-basing the dark palette, and an earlier draft that did
// so picked a dark info.main byte-identical to dark primary.light.
//
// Every `main` is >= 4.5:1 against its contrastText AND against both light
// surfaces (#ffffff card, #f7f8fa page ground). Asserted in
// src/__tests__/theme.tokens.test.js.
//
// These are literal hexes or indexes into UNCHANGED tokensDark ramps, so
// reverseTokens() never sees them and the derived light ramps cannot shift.
// ---------------------------------------------------------------------------
const semanticLight = {
  error: {
    main: tokensDark.primary1[500], // #af2713 OpsBlox red - 6.73:1 on white
    light: tokensDark.primary1[400], // #bb4736
    dark: tokensDark.primary1[700], // #9f1d0e
    contrastText: tokensDark.grey[0], // #ffffff
  },
  warning: {
    main: "#8a5a00", // deepened brand amber - 5.93:1 on white
    light: tokensDark.secondary3[300], // #ffe3a3
    dark: "#665429",
    contrastText: tokensDark.grey[0], // #ffffff
  },
  info: {
    main: "#026aa7", // deepened brand cyan - 5.80:1 on white
    light: tokensDark.primary[100], // #b3e7fb brand tint
    dark: "#014e7c",
    contrastText: tokensDark.grey[0], // #ffffff
  },
  success: {
    main: tokensDark.secondary[500], // #14842f brand green - 4.80:1 on white
    light: tokensDark.secondary[400], // #37964e
    dark: tokensDark.secondary[700], // #0e7123
    contrastText: tokensDark.grey[0], // #ffffff
  },
};

// ---------------------------------------------------------------------------
// Markdown callout surfaces, consumed by NiceMarkdown.jsx:114/150/185.
//
// Hoisted out of the light-only branch and spread into BOTH modes. In dark
// mode theme.palette.markdownNote was `undefined`, so NiceMarkdown.jsx:114
// threw "Cannot read properties of undefined (reading 'main')" for any dark
// user meeting a markdown callout - including ConfirmationDialog and
// InfoDialogue. Values are byte-identical to the previous light definitions.
//
// markdownTip's pale brand blue is intentional, not a copy of the
// secondary.main defect.
// ---------------------------------------------------------------------------
const markdownCallouts = {
  markdownNote: {
    main: "#EFD9FD",
    contrastText: tokensDark.grey[900],
  },
  markdownTip: {
    main: tokensDark.primary[100],
    contrastText: tokensDark.grey[900],
  },
  markdownWarning: {
    main: tokensDark.secondary3[300],
    contrastText: tokensDark.grey[900],
  },
};

// mui theme settings
export const themeSettings = (mode) => {
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // palette values for dark mode - deliberately unchanged (spec s.6)
            // with exactly two exceptions:
            //  1. markdownCallouts, which existed only in the light branch, so
            //     theme.palette.markdownNote.main threw in dark mode.
            //  2. neutral.dark and neutral.contrastText, previously undefined
            //     here. <Button color="neutral" variant="contained">
            //     (StateSetFormActions.jsx, StateSetFieldFormActions.jsx) reads
            //     .contrastText for its label and .dark for its hover, and
            //     `neutral` is not run through augmentColor.
            primary: {
              ...tokensDark.primary,
              main: tokensDark.primary[400],
              light: tokensDark.primary[300],
            },
            secondary: {
              ...tokensDark.secondary,
              main: tokensDark.secondary[300],
            },
            neutral: {
              ...tokensDark.grey,
              main: tokensDark.grey[500],
              dark: tokensDark.grey[600],
              contrastText: tokensDark.grey[0],
            },
            background: {
              default: tokensDark.grey[900],
              alt: tokensDark.grey[800],
              light: tokensDark.grey[800],
              paper: tokensDark.grey[800],
            },
            action:{
              hover: tokensDark.grey[700],
            },
            text:{
              primary: tokensDark.grey[100],
              secondary: tokensDark.grey[300],
              light: tokensDark.grey[400],
            },
            ...markdownCallouts,
          }
        : {
            // palette values for light mode
            primary: {
              ...tokensLight.primary,
              // The OpsBlox brand cyan, restored deliberately: it is the
              // company's colour and carrying it is the point.
              //
              // Known cost, accepted: #00b0f0 measures 2.48:1 on white, below
              // WCAG AA for text (4.5:1) and below AA-large (3:1), so link text
              // and the labels on filled primary buttons do not meet the
              // standard. `dark` below is the accessible variant, available to
              // any surface that needs to clear AA without leaving the brand.
              main: brandTint, // #00b0f0
              light: tokensDark.primary[100], // #b3e7fb
              dark: "#0b6bcb", // 5.28:1 on white -- the AA-passing brand blue
              contrastText: tokensDark.grey[0], // #ffffff
            },
            secondary: {
              ...tokensLight.secondary,
              main: tokensDark.secondary[500], // #14842f brand green - 4.80:1
              light: tokensDark.secondary[400], // #37964e
              dark: tokensDark.secondary[700], // #0e7123
              contrastText: tokensDark.grey[0], // #ffffff - dark text is 3.83:1
            },
            neutral: {
              ...tokensLight.grey,
              main: tokensDark.grey[300],
              dark: tokensDark.grey[400],
              // grey[900], not grey[100]: <Button color="neutral" variant="contained">
              // (StateSetFormActions, StateSetFieldFormActions) reads this for its
              // label. Against main #a3a3a3 the old light-grey was 1.91:1 -- below
              // even AA-large. Near-black is 7.30:1.
              contrastText: tokensDark.grey[900],
            },
            background: {
              default: "#f7f8fa", // page ground, so cards can be white
              paper: tokensDark.grey[0], // #ffffff card surface
              alt: tokensDark.primary[300],
              light: tokensDark.grey[50],
              contrastText: tokensDark.grey[100], // Define contrast text
            },
            action:{
              hover: tokensDark.primary[100],
            },
            text:{
              primary: tokensDark.grey[900],
              secondary: tokensDark.grey[800],
              light: tokensDark.grey[100],
            },
            ...semanticLight,
            ...markdownCallouts,
          }),
    },
    
    shape: {
      borderRadius: 8,
    },

    typography: {
      // One declaration only - MUI re-injects this onto every variant, so
      // h1-h6 inherit it. "Inter Variable" is the family @fontsource-variable
      // registers; "Segoe UI" survives as a Windows fallback.
      fontFamily: [
        "Inter Variable",
        "Inter",
        "system-ui",
        "-apple-system",
        '"Segoe UI"',
        "sans-serif",
      ].join(","),
      // Kept as the NUMBER 14: NiceMarkdown.jsx:129/163/200 reads
      // theme.typography.fontSize straight into an inline style.
      fontSize: 14,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      // rem scale against a 16px root (index.css sets no html font-size).
      // Negative tracking at the large end; body stays 14px for density.
      h1: { fontSize: "2rem", fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.02em" }, // 32px
      h2: { fontSize: "1.625rem", fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.018em" }, // 26px
      h3: { fontSize: "1.375rem", fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.015em" }, // 22px
      h4: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.35, letterSpacing: "-0.01em" }, // 18px
      h5: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.4, letterSpacing: "-0.005em" }, // 16px
      h6: { fontSize: "0.9375rem", fontWeight: 500, lineHeight: 1.45, letterSpacing: 0 }, // 15px
      subtitle1: { fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.5 }, // 14px
      subtitle2: { fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.5 }, // 13px
      body1: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.55 }, // 14px
      // body2 must stay 14px. @mui/x-data-grid spreads theme.typography.body2
      // onto the grid root (GridRootStyles.js:123), so 13px here would shrink
      // every DataGrid in this grid-first app with no test failure.
      body2: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.43 }, // 14px
      button: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.6,
        letterSpacing: 0,
        // Owned HERE, not on components.MuiButton - this also de-capitalises
        // ToggleButton and anything else applying theme.typography.button.
        // Plan 1b's MuiButton override must not re-declare it.
        textTransform: "none",
      },
      caption: { fontSize: "0.75rem", fontWeight: 400, lineHeight: 1.45 }, // 12px
      overline: {
        fontSize: "0.6875rem", // 11px
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
    },
    // =======================================================================
    // Stage 1b - component overrides.
    //
    // Every entry below is keyed to a component this app actually renders.
    // The invariants are asserted in src/__tests__/theme.components.test.jsx,
    // which renders each one in both modes and reads the emitted CSS back.
    // Rules of the block:
    //
    //  * NO MuiPaper.styleOverrides.root. Paper is the base of Menu, Popover,
    //    Dialog, Drawer, Alert, Card, Accordion, the Autocomplete listbox
    //    surface, the DataGrid panel and every date-picker popper.
    //  * NO textTransform on MuiButton - typography.button owns it (Stage 1a).
    //  * NO `theme.palette.action.hover` in light mode: this theme sets it to
    //    the SOLID brand cyan #b3e7fb, so anything keyed on it reads as a
    //    filled cyan block, not a tint. Hover tints below are literal rgba.
    //  * Callback form `({ theme }) => ({...})` only - no `alpha()` import.
    // =======================================================================
    components: {
      // --- Buttons ---------------------------------------------------------
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          // textTransform intentionally absent - see typography.button.
          root: {
            borderRadius: 8,
            paddingLeft: 16,
            paddingRight: 16,
          },
          sizeSmall: { paddingLeft: 12, paddingRight: 12 },
          sizeLarge: { paddingLeft: 22, paddingRight: 22 },
          // No `contained: { boxShadow: "none" }`: defaultProps.disableElevation
          // already emits exactly that, and doing it in styleOverrides too
          // would stop a call site opting back in with disableElevation={false}.
        },
      },

      // --- Cards -----------------------------------------------------------
      MuiCard: {
        styleOverrides: {
          // ownerState.variant is Card's own prop (Card.js builds ownerState
          // from props), so outlined cards keep Paper's shadowless border
          // instead of gaining a drop shadow they never had.
          root: ({ ownerState, theme }) => ({
            borderRadius: 12,
            backgroundImage: "none",
            ...(ownerState.variant !== "outlined" && {
              boxShadow:
                theme.palette.mode === "light"
                  ? "0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.06)"
                  : "0 1px 2px rgba(0,0,0,0.40), 0 6px 18px rgba(0,0,0,0.45)",
            }),
          }),
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          // `title` is DELIBERATELY not overridden. CardHeader only attaches
          // .MuiCardHeader-title when it wraps the title itself, and it skips
          // wrapping when title.type === Typography (CardHeader.js guards on
          // `title.type !== Typography`). Of the
          // four CardHeaders in this app, ContributionCard.jsx:84 passes a
          // <Typography> element, so a `title` rule would land on three of
          // four. It would also outrank the titleTypographyProps variant the
          // other call sites ask for, because overridesResolver hangs
          // styles.title off the ROOT as `& .MuiCardHeader-title` (0,2,0)
          // which beats .MuiTypography-h5 (0,1,0).
          root: { padding: "16px 16px 8px" },
          subheader: ({ theme }) => ({ color: theme.palette.text.secondary }),
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: "8px 16px 16px",
            "&:last-child": { paddingBottom: 16 },
          },
        },
      },

      // --- Accordion -------------------------------------------------------
      MuiAccordion: {
        defaultProps: { disableGutters: true },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            boxShadow: "none",
            backgroundImage: "none",
            "&:before": { display: "none" },
            "& + &": { marginTop: 8 },
          }),
        },
      },

      // --- Chip ------------------------------------------------------------
      MuiChip: {
        // No defaultProps.size: 23 of the 29 live <Chip> sites already pass
        // size="small" by hand, and a global default would silently shrink the
        // other six (NavBarSearch.jsx:119 x2, NiceTrend.jsx:62/66/70,
        // indexCatalogue.jsx:341). fontSize is left to MUI entirely - Chip
        // small is 0.75rem, exactly the 12px floor typography.caption sets,
        // and no lower.
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500 },
          label: { paddingLeft: 8, paddingRight: 8 },
        },
      },

      // --- Tabs ------------------------------------------------------------
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
          indicator: { height: 2, borderRadius: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          // Guarded on the icon+label branch. Tab.js gives a Tab that has BOTH
          // an icon and a label minHeight 72 / padding 9px, inside its base
          // styled() call - i.e. BEFORE styleOverrides - so an unconditional
          // minHeight here would silently flatten every icon tab.
          root: ({ ownerState }) => ({
            fontWeight: 500,
            ...(!(ownerState.icon && ownerState.label) && {
              minHeight: 44,
              paddingTop: 10,
              paddingBottom: 10,
            }),
          }),
        },
      },

      // --- Inputs ----------------------------------------------------------
      MuiInputBase: {
        styleOverrides: {
          // 14px to match body1/body2; MUI's own default is 1rem.
          root: { fontSize: "0.875rem" },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            // The :not() guards are load-bearing. Without them this rule sits
            // after MUI's base Mui-focused / Mui-error / Mui-disabled outline
            // rules at equal specificity and wins, so a focused or invalid
            // field would lose its ring on hover.
            "&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline":
              {
                borderColor: theme.palette.text.secondary,
              },
          }),
          // NO `notchedOutline` slot. This palette never defines
          // `palette.divider`, so `theme.palette.divider` here resolved to
          // MUI's default rgba(0,0,0,0.12) and REPLACED the base resting
          // outline of rgba(0,0,0,0.23) - fainter (1.32:1 on white, down from
          // 1.74:1) across ~214 TextField, ~24 Autocomplete and ~10 Select
          // sites. Leaving the slot off restores the base colour. Picking a
          // better one is a design decision, not a theme-plumbing one.
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: ({ theme }) => ({ color: theme.palette.text.secondary }),
        },
      },

      // --- Autocomplete ----------------------------------------------------
      MuiAutocomplete: {
        styleOverrides: {
          // `option` is NOT used: its overridesResolver hangs it off the
          // Popper as `& .MuiAutocomplete-option`, which ties on specificity
          // with the listbox's own `& .MuiAutocomplete-option` rules and
          // loses. Nesting under `listbox` puts it in the same styled()
          // as those rules, after them, so it wins.
          //
          // minHeight is left alone: the base rules carry a
          // `@media (min-width:600px){minHeight:auto}` that a flat minHeight
          // here would clobber, the same trap as MenuItem.
          listbox: ({ theme }) => ({
            padding: 4,
            "& .MuiAutocomplete-option": {
              borderRadius: 6,
              marginLeft: 4,
              marginRight: 4,
              paddingLeft: 10,
              paddingRight: 10,
              "&.Mui-focused": {
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "rgba(11, 107, 203, 0.08)"
                    : "rgba(255, 255, 255, 0.08)",
              },
            },
          }),
          paper: { borderRadius: 10, backgroundImage: "none" },
        },
      },

      // --- Overlays --------------------------------------------------------
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor:
              theme.palette.mode === "light" ? "#1f2937" : "#3d3d3d",
            color: "#ffffff",
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: 1.45,
            borderRadius: 6,
            padding: "6px 10px",
          }),
          arrow: ({ theme }) => ({
            color: theme.palette.mode === "light" ? "#1f2937" : "#3d3d3d",
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          // MuiDialog.paper, never MuiPaper.root.
          paper: { borderRadius: 12, backgroundImage: "none" },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundImage: "none",
            borderColor: theme.palette.divider,
          }),
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "inherit" },
        styleOverrides: {
          root: { backgroundImage: "none" },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 10, backgroundImage: "none" },
          list: { paddingTop: 4, paddingBottom: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          // NO minHeight. MenuItem.js ends with
          // `!dense && { [breakpoints.up('sm')]: { minHeight: 'auto' } }`;
          // a flat minHeight in styleOverrides is emitted after that media
          // block and beats it at every viewport >= 600px, resizing all 109
          // MenuItems in the app.
          root: ({ theme }) => ({
            borderRadius: 6,
            marginLeft: 4,
            marginRight: 4,
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? "rgba(11, 107, 203, 0.08)"
                  : "rgba(255, 255, 255, 0.08)",
            },
            "&.Mui-selected": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? "rgba(11, 107, 203, 0.12)"
                  : "rgba(38, 188, 242, 0.16)",
            },
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 6,
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? "rgba(11, 107, 203, 0.08)"
                  : "rgba(255, 255, 255, 0.08)",
            },
            "&.Mui-selected": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? "rgba(11, 107, 203, 0.12)"
                  : "rgba(38, 188, 242, 0.16)",
            },
          }),
        },
      },

      // --- Text / rules ----------------------------------------------------
      MuiLink: {
        // 12 of the 15 MUI <Link> sites already pass underline="hover" by
        // hand. The three that do not are the two logo wrappers
        // (Footer.jsx:82, Navbar.jsx:128, both wrapping an <img>) and
        // SimpleBreadcrumbs.jsx:29, whose own call sites all pass it - so
        // this default changes nothing visible and removes the duplication.
        defaultProps: { underline: "hover" },
        // NO styleOverrides.root. Link is styled(Typography), so its class is
        // emitted AFTER MuiTypography-h3/h4, and a fontWeight here overrides
        // the variant the call site asked for: measured, `<Link variant="h4">`
        // dropped from 600 to 500. That hits six Footer links (:107/:117/:126/
        // :158/:167/:176); Footer.jsx:221 and :232 carry no variant, so a
        // fontWeight would RAISE those from 400 -- wrong in both directions.
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor:
              theme.palette.mode === "light"
                ? "#e4e7ec"
                : "rgba(255, 255, 255, 0.12)",
          }),
        },
      },

      // --- Tables ----------------------------------------------------------
      MuiTableCell: {
        styleOverrides: {
          // The size/padding guard matters: TableCell.js puts
          // `size === 'small' -> padding: '6px 16px'`,
          // `padding === 'checkbox' -> padding: '0 0 0 4px'` and
          // `padding === 'none' -> padding: 0` in its BASE styled() call, so a
          // flat `padding` here would flatten all three - including the
          // TableCell that TablePagination renders for itself.
          root: ({ ownerState, theme }) => ({
            borderBottomColor:
              theme.palette.mode === "light"
                ? "#e4e7ec"
                : "rgba(255, 255, 255, 0.12)",
            ...(ownerState.size !== "small" &&
              ownerState.padding === "normal" && {
                paddingTop: 10,
                paddingBottom: 10,
              }),
          }),
          head: ({ theme }) => ({
            fontWeight: 600,
            lineHeight: 1.4,
            color: theme.palette.text.secondary,
            backgroundColor:
              theme.palette.mode === "light" ? "#f7f8fa" : "#323232",
          }),
        },
      },
      // NO MuiTableRow. Row hover is deliberately NOT themed: MUI only emits
      // the .MuiTableRow-hover class when a <TableRow> is given the `hover`
      // prop, and not one of the 8 hand-built tables in this app passes it
      // (ContributorDataGrid, GroupDataGrid, TeamDataGrid, SavingsPlanTable,
      // NiceFormDisplay, ContributorMergedTable, EditableFieldForm x2), so a
      // `&.MuiTableRow-hover:hover` rule here changes zero pixels. Adding the
      // `hover` props to make it bite is a design change, not a theme change.
      //
      // If this family is ever reinstated: NO
      // `&:last-child .MuiTableCell-root { borderBottom: 0 }`. A <TableHead>
      // whose single <TableRow> is the last child of the thead matches that
      // selector, so it would delete the header underline on all 8 of them.

      // --- Alert -----------------------------------------------------------
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 8, alignItems: "center", backgroundImage: "none" },
          message: { paddingTop: 6, paddingBottom: 6 },
        },
      },
    },


  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});



export const useMode = () => {
  // Get the color mode preference from Redux state
  const colorModePreference = useSelector((state) => {
    const preference =
      state.auth.user?.preferences?.find(
        (preference) => preference.name === "useDarkMode"
      )?.preferenceValue;

    return preference ? preference : null;
  });

  const initialMode = colorModePreference === "dark" ? "dark" : "light";

  const [mode, setMode] = useState(initialMode);

  const colorMode = useMemo(() => {
    const toggleColorMode = () =>
      setMode((prev) => (prev === "light" ? "dark" : "light"));

    return {
      toggleColorMode,
    };
  }, [setMode]);


  const mergedTypography = {
    ...themeSettings(mode).typography,
    //...chatTypography,
  };

  const mergedThemeSettings = {
    ...themeSettings(mode),
    typography: mergedTypography,
  };

  //debugger
  const theme = useMemo(() => createTheme(mergedThemeSettings), [mode]);

  return [theme, colorMode];
};